import React, {
  Fragment,
  useRef,
  useState,
  useContext,
  createContext,
  useEffect,
} from "react";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import Papa from "papaparse";

import {
  Row,
  Col,
  Card,
  Table,
  Badge,
  Dropdown,
  Modal,
  ProgressBar,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  usePagination,
} from "react-table";
import loadingCircleImg from "../../../../images/misc/loading-circle-green.svg";
import landmarkImg from "../../../../images/landmark/logo-text-dark.png";
import ApiService from "../../../../services/ApiService";
import PageHeaderBar from "../../../layouts/PageHeaderBar";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import PersonIcon from "@mui/icons-material/Person";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { styled } from "@mui/material/styles";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { utils } from "ethers";
import { MaterialReactTable } from "material-react-table";
import { ExportToCsv } from "export-to-csv";
import { Box, Button } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { FileCopyOutlined as FileCopyOutlinedIcon } from "@mui/icons-material";
import DoneIcon from "@mui/icons-material/Done";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { darken } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { TypeAnimation } from "react-type-animation";
import { copyTextToClipboard, timeNow, useDocumentTitle } from "../../../utils";
import { ThemeContext } from "../../../../context/ThemeContext";
import {
  addCommas,
  epochToDateString,
  epochToDateTimeString,
  stringToRGB,
  timeAgo,
  toTitleCase,
  toTitleCaseByWord,
} from "../../../../utils";

import SeamlessBotImg from "../../../../images/misc/ceo-sniper-logo.png";
import BlueLoading from "../../../../images/misc/blue-goo-loading.svg";
import DefaultProfileImg from "../../../../images/misc/default-profile-silhouette.svg";
import DefaultCompanyImg from "../../../../images/misc/default-company-silhouette.svg";
import { Parser } from "@json2csv/plainjs";
// import { timeNow, useDocumentTitle } from "../../../utils";
import { useAuth } from "../../../Providers/LandmarkAuthContext";
import EmployeeInfo from "./EmployeeInfo";

const SeamlessAccounts = [
  "ihaq@landmarkventures.com",
  "ibrown@landmarkventures.com",
  "dsoukup@landmarkventures.com",
  "Sniessl@landmarkventures.com",
  "ptemplet@landmarkventures.com",
];

const BatchIntervals = [
  "6 hours",
  "12 hours",
  "24 hours",
  "48 hours",
  "72 hours",
  "Weekly",
];

// Images scraped by the bot are stored on the bots server and returned as a
// path relative to its root (e.g. /uploads/landmark/ceoSniperImages/<file>).
// Older tasks may still hold an absolute (and probably expired) LinkedIn CDN url.
const BOTS_API_ROOT = new ApiService({ botsApiUrl: true }).apiUrlRoot;
const DEFAULT_PFP_IMG = {
  ceo: DefaultProfileImg,
  company: DefaultCompanyImg,
};

function resolveCeoSniperImgUrl(img) {
  if (!img || typeof img !== "string") return "";
  const trimmed = img.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("data:")) {
    return trimmed;
  }
  return `${BOTS_API_ROOT}${trimmed.replace(/^\/+/, "")}`;
}

// <img className="pfp"> that falls back to a silhouette when the task has no
// image or the stored/linked image fails to load (dead LinkedIn CDN url etc.)
const CeoSniperPfp = ({ src, kind = "ceo", alt = "", className = "pfp" }) => {
  const fallback = DEFAULT_PFP_IMG[kind] || DefaultProfileImg;
  const resolved = resolveCeoSniperImgUrl(src);
  const [failedSrc, setFailedSrc] = useState("");
  const useFallback = !resolved || failedSrc === resolved;
  return (
    <img
      className={`${className}${useFallback ? " pfp-placeholder" : ""}`}
      src={useFallback ? fallback : resolved}
      alt={alt}
      loading="lazy"
      onError={() => {
        if (!useFallback) setFailedSrc(resolved);
      }}
    />
  );
};

function mapEmails(arr, emailKey, setEmailKey = null) {
  const emailsSet = new Set();

  arr.forEach((obj) => {
    Object.keys(obj).forEach((key) => {
      if (
        typeof obj[key] === "string" &&
        /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,7}\b/.test(obj[key])
      ) {
        const email = obj[key].toLowerCase().trim();
        emailsSet.add(email);
        if (setEmailKey && emailKey !== key) {
          setEmailKey(key);
        }
      }
    });
  });

  return Array.from(emailsSet);
}

const SeamlessBotForm = ({ employee }) => {
  const [notes, setNotes] = useState("");
  const [sourceOfLead, setSourceOfLead] = useState("");
  const [referencePersonName, setReferencePersonName] = useState("");
  const [referencePersonTitle, setReferencePersonTitle] = useState("");
  const [referenceCompany, setReferenceCompany] = useState("");
  const [companyUrl, setCompanyUrl] = useState("");
  const [allTasks, setAllTasks] = useState([]);
  const [manualOverrideModal, setManualOverrideModal] = useState({
    open: false,
    taskId: "",
    mode: "",
    url: "",
  });

  async function fetchAllTasks() {
    try {
      const api = new ApiService({ employee, botsApiUrl: true });
      const taskArr = await api.get(`landmark/ceosniper/getAllTasks`);
      // ApiService swallows errors and returns an object (e.g. {message: "Unauthorized"}
      // or {error: "Request timed out"}) instead of throwing
      if (!Array.isArray(taskArr)) {
        const reason = taskArr?.error || taskArr?.message || "Unknown error";
        console.error(`fetchAllTasks() failed:`, taskArr);
        toast.error(`Failed to load CEO Sniper tasks (${reason})`);
        return;
      }
      setAllTasks(taskArr.reverse());
      console.log(`allTasks`, taskArr);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load CEO Sniper tasks");
    }
  }
  useEffect(() => {
    fetchAllTasks();
  }, []);

  const openManualOverrideModal = (taskId, mode, currentUrl = "") => {
    setManualOverrideModal({
      open: true,
      taskId,
      mode,
      url: currentUrl || "",
    });
  };

  const closeManualOverrideModal = () => {
    setManualOverrideModal({
      open: false,
      taskId: "",
      mode: "",
      url: "",
    });
  };

  const getManualOverrideValidationError = (mode, url) => {
    const trimmedUrl = (url || "").trim();
    if (!trimmedUrl) {
      return "Please enter a LinkedIn URL.";
    }
    const isCompanyMode = mode === "company";
    const requiredPath = isCompanyMode ? "linkedin.com/company/" : "linkedin.com/in/";
    if (!trimmedUrl.toLowerCase().includes(requiredPath)) {
      return isCompanyMode
        ? "Please enter a valid LinkedIn company URL."
        : "Please enter a valid LinkedIn profile URL.";
    }
    return "";
  };

  const handleSubmitManualOverride = async () => {
    const { taskId, mode, url } = manualOverrideModal;
    const trimmedUrl = url.trim();
    if (!taskId || !mode) {
      toast.error("Invalid task.");
      return;
    }
    if (!trimmedUrl) {
      toast.error("Please enter a LinkedIn URL.");
      return;
    }
    const validationError = getManualOverrideValidationError(mode, trimmedUrl);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    const isCompanyMode = mode === "company";

    try {
      const api = new ApiService({ employee, botsApiUrl: true });
      const endpoint = isCompanyMode
        ? `landmark/ceosniper/manuallyAddCompanyLinkedInUrl`
        : `landmark/ceosniper/manuallyAddCeoUrl`;
      const payload = isCompanyMode
        ? { taskId, companyLinkedInUrl: trimmedUrl, employee }
        : { taskId, ceoUrl: trimmedUrl, employee };
      const { success, error } = await api.post(endpoint, payload);
      if (!success) {
        toast.error(`${error}`);
        return;
      }
      toast.success(success, { duration: 6000 });
      closeManualOverrideModal();
      fetchAllTasks();
    } catch (error) {
      console.error(error);
      toast.error("Error submitting LinkedIn URL override.");
    }
  };

  const handleRunBotBtn = async () => {
    if (!companyUrl) {
      toast.error("Please enter a valid Company URL");
      return;
    }
    if (!referenceCompany || !referenceCompany.length) {
      toast.error("Please enter a Reference Company");
      return;
    }
    if (!isRunBtnEnabled) {
      return;
    }
    try {
      const api = new ApiService({ employee, botsApiUrl: true });
      const defaultLinkedInEmail = "mjonezdev@gmail.com";
      const { success, error } = await api.post(
        `landmark/ceosniper/createTask`,
        {
          employee,
          referencePersonName,
          referencePersonTitle,
          referenceCompany,
          companyUrl,
          notes,
          linkedInEmail: defaultLinkedInEmail,
        }
      );
      if (!success) {
        toast.error(`${error}`);
        return false;
      } else {
        toast.success(`${success}`, { duration: "6000" });
        // toast.success(`Results will be emailed to ${resultsEmail}`, {
        //   duration: "6000",
        // });
        // clear fields
        // setSearchTitle("");
        setCompanyUrl("");
        setReferencePersonName("");
        setReferencePersonTitle("");
        setReferenceCompany("");
        setNotes("");
        return true;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const retryTask = async (taskId) => {
    try {
      if (!taskId) {
        toast.error("Task ID is required.");
        return false;
      }

      const api = new ApiService({ employee, botsApiUrl: true });
      const { success, error } = await api.post(
        `landmark/ceosniper/retryTask`,
        {
          employee, // Assumes `employee` is accessible in the current scope
          taskId,
        }
      );

      if (!success) {
        toast.error(`${error}`);
        return false;
      } else {
        toast.success(`${success}`, { duration: 6000 });
        // Optionally, refresh list or perform other actions after retry
        fetchAllTasks();
        return true;
      }
    } catch (error) {
      console.error("Error while retrying task:", error);
      toast.error("An unexpected error occurred while retrying the task.");
      return false;
    }
  };

  const setTaskHandled = async (taskId, handled) => {
    try {
      const api = new ApiService({ employee, botsApiUrl: true });
      const { success, error } = await api.post(
        `landmark/ceosniper/setTaskHandled`,
        {
          employee,
          taskId,
          handled,
        }
      );
      if (!success) {
        toast.error(`${error}`);
        return false;
      } else {
        toast.success(`${success}`, { duration: "6000" });
        // refresh list
        fetchAllTasks();
        return true;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const deleteTask = async (taskId) => {
    try {
      if (!taskId) {
        toast.error("Task ID is required.");
        return false;
      }
      const confirmed = window.confirm(
        "Delete this CEO Sniper task? This cannot be undone."
      );
      if (!confirmed) return false;

      const api = new ApiService({ employee, botsApiUrl: true });
      const { success, error } = await api.post(
        `landmark/ceosniper/deleteTask`,
        {
          employee,
          taskId,
        }
      );
      if (!success) {
        toast.error(`${error}`);
        return false;
      } else {
        toast.success(`${success}`, { duration: "6000" });
        fetchAllTasks();
        return true;
      }
    } catch (error) {
      console.error(error);
      toast.error("An unexpected error occurred while deleting the task.");
      return false;
    }
  };

  let isRunBtnEnabled = false;

  if (companyUrl && referenceCompany && referenceCompany.length) {
    isRunBtnEnabled = true;
  }
  const manualOverrideValidationError = getManualOverrideValidationError(
    manualOverrideModal.mode,
    manualOverrideModal.url,
  );
  const disableManualOverrideSubmit = Boolean(manualOverrideValidationError);

  return (
    <>
      {/* <div className="bot-downtime-msg">
        03/13/24 - Bot is experiencing downtime due to LinkedIn/Seamless
        changing the way contacts are displayed
      </div> */}
      <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
        <Card className="mb-3 h-fc bot-table">
          <div className="logo-container">
            <img src={SeamlessBotImg} className="img-fluid" />
          </div>
          <div className="sub-logo-text-container">
            <div>
              • Provide the bot with the website of the company
              <br />
              • The bot will attempt to find the CEO of the company, and add
              them to a Sales Navigator list
              <br />
            </div>
          </div>
          <div className="row">
            <>
              <div className="seamless-bot-form-wrapper">
                <div className="form-section text-left">
                  {/* <div className="events-owners-subheading text-center">
                    Enter your LinkedIn credentials
                    <br />
                    (account must have Sales access)
                  </div>
                  <div className="form-group mb-3 col-12">
                    <label className="form-label">
                      Email <span className="example-text"></span>
                    </label>
                    <input
                      type="text"
                      value={linkedInUsername}
                      className="form-control"
                      onChange={(e) =>
                        handleSetLinkedInUsername(e.target.value)
                      }
                      autoComplete="email"
                    />
                  </div>
                  <div className="form-group mb-3 col-12">
                    <label className="form-label">
                      Password <span className="example-text"></span>
                    </label>
                    <input
                      type="employee"
                      value={linkedInPassword}
                      className="form-control"
                      onChange={(e) => setLinkedInPassword(e.target.value)}
                      autoComplete="linkedInPassword"
                    />
                  </div> */}
                  {/* <div className="events-owners-subheading text-center">
                    Enter LinkedIn URLs
                    <br />
                  </div> */}
                  <div className="form-group mb-3 col-12">
                    <label className="form-label">
                      Company Website <br />
                      <span className="example-text">
                        e.g. https://www.bastille.net/
                      </span>
                    </label>
                    <input
                      type="text"
                      value={companyUrl}
                      className="form-control"
                      onChange={(e) => setCompanyUrl(e.target.value)}
                    />
                    <br></br>
                    <label className="form-label">
                      Reference Company <br />
                      <span className="example-text">
                        The company of the person who gave you this lead
                      </span>{" "}
                      <br />
                      <span className="example-text">
                        {/* If provided, will be used to create a personalized
                        outreach message.
                        <br /> */}
                        e.g. "HIG Capital" or "Dyson"
                      </span>
                    </label>
                    <input
                      type="text"
                      value={referenceCompany}
                      className="form-control"
                      onChange={(e) =>
                        setReferenceCompany(toTitleCaseByWord(e.target.value))
                      }
                    />
                    <br></br>{" "}
                    <label className="form-label">
                      Reference Person Title{" "}
                      <span className="example-text">(optional)</span> <br />
                      <span className="example-text">
                        The job title of the person who gave you this lead
                      </span>{" "}
                      <br />
                      <span className="example-text">
                        {/* If provided, will be used to create a personalized
                        outreach message.
                        <br /> */}
                        e.g. "CMO" or "Marketing Director"
                      </span>
                    </label>
                    <input
                      type="text"
                      value={referencePersonTitle}
                      className="form-control"
                      onChange={(e) =>
                        setReferencePersonTitle(
                          toTitleCaseByWord(e.target.value)
                        )
                      }
                    />
                    <br></br>
                    <label className="form-label">
                      Reference Person Name{" "}
                      <span className="example-text">(optional)</span> <br />
                      <span className="example-text">
                        The name of the person who gave you this lead
                      </span>{" "}
                      <br />
                      <span className="example-text">
                        {/* If provided, will be used to create a personalized
                        outreach message.
                        <br /> */}
                        e.g. "Mark Fiske" or "Amy Adams"
                      </span>
                    </label>
                    <input
                      type="text"
                      value={referencePersonName}
                      className="form-control"
                      onChange={(e) =>
                        setReferencePersonName(toTitleCase(e.target.value))
                      }
                    />
                    <br></br>
                    <label className="form-label">
                      Note <span className="example-text">(optional)</span>{" "}
                      <br />
                      <span className="example-text">
                        Anything else you'd like to mention. For our reference
                        only.
                        <br />
                        e.g. "from a tap with jon guy at apple"
                      </span>
                    </label>
                    <input
                      type="text"
                      value={notes}
                      className="form-control"
                      onChange={(e) => setNotes(e.target.value)}
                    />
                    <br></br>
                  </div>

                  <div className="d-flex justify-content-center align-items-center">
                    <button
                      className={`run-btn btn btn-primary ${
                        isRunBtnEnabled ? "" : "disabled-soft"
                      }`}
                      onClick={handleRunBotBtn}
                    >
                      {`RUN BOT`}
                      <i className="event-owner-add fa-sharp fa-solid fa-robot ml-05"></i>
                    </button>
                  </div>
                </div>
              </div>
            </>
          </div>
        </Card>
        {allTasks && allTasks.length ? (
          <div className="ceo-sniper-tasks-section">
            <div className="header-text">Recent Jobs</div>
            <div className="tasks-container">
              {allTasks && allTasks.length
                ? allTasks.map((task, idx) => {
                    const {
                      taskId,
                      employeeName,
                      employeeEmail,
                      notes,
                      companyUrl,
                      companyLinkedInUrl,
                      linkedInEmail,
                      ceoInfo: {
                        name: ceoName,
                        bio: ceoBio,
                        img: ceoImg,
                        jobSummary: ceoJobSummary,
                        jobTitle: ceoJobTitle,
                        companyUrl: ceoCompanyUrl,
                        salesNavUrl: ceoSalesNavUrl,
                      } = {},
                      companyInfo: {
                        name: companyName,
                        summary: companySummary,
                        img: companyImg,
                        companyUrl: companyInfoUrl,
                        ceoUrl: companyCeoUrl,
                      } = {},
                      ceoAddedToSalesNav,
                      completed,
                      createdTime,
                      errorMsg,
                      handled,
                    } = task;

                    // used for display logic
                    let linkedInFound =
                      companyLinkedInUrl &&
                      companyLinkedInUrl
                        .toLowerCase()
                        .includes("linkedin.com/company/");
                    let linkedInParseSuccess =
                      task.companyInfo && task.companyInfo.name;
                    let ceoFound =
                      companyCeoUrl &&
                      companyCeoUrl.toLowerCase().includes("linkedin.com/in/");
                    let ceoParseSuccess = task.ceoInfo && task.ceoInfo.name;

                    if (ceoFound) {
                      linkedInFound = true;
                      if (!linkedInParseSuccess) {
                        linkedInParseSuccess = true;
                      }
                    }

                    // linkedInFound = true;
                    // linkedInParseSuccess = false;
                    // ceoFound = true;
                    // ceoParseSuccess = false;

                    function getLinkedInShortUrl(url) {
                      return url.replace("https://www.", "");
                    }

                    return (
                      <div
                        className="task-outer-container"
                        key={taskId || idx}
                      >
                        <div className="task-container">
                          <a href={companyUrl} target="_blank" className="info">
                            {companyUrl}
                          </a>
                          {notes ? (
                            <div className="info">
                              <b>{notes}</b>
                            </div>
                          ) : null}
                          {employeeEmail ? (
                            <div className="employee-name-container">
                              <div className="employee-name">
                                <b>
                                  {employeeEmail
                                    .toLowerCase()
                                    .replace("@landmarkventures.com", "")}
                                </b>{" "}
                                -{" "}
                                {epochToDateString(createdTime, {
                                  simpleUS: true,
                                })}
                              </div>
                            </div>
                          ) : null}
                        </div>
                        <div className="arrow">
                          <i className="fa-solid fa-arrow-right"></i>
                        </div>
                        <div className="task-container">
                          {linkedInFound ? (
                            linkedInParseSuccess ? (
                              <div className="inner-container">
                                <CeoSniperPfp
                                  src={companyImg}
                                  kind="company"
                                  alt={companyName || "Company"}
                                />
                                <div>
                                  <div className="info highlighted">
                                    {companyName}
                                  </div>
                                  <div className="info">{companySummary}</div>
                                  <a
                                    href={companyLinkedInUrl}
                                    target="_blank"
                                    className="info"
                                  >
                                    {getLinkedInShortUrl(companyLinkedInUrl)}
                                  </a>
                                </div>
                              </div>
                            ) : (
                              <div className="error-msg">
                                Error scraping LinkedIn company data
                              </div>
                            )
                          ) : (
                            <div className="error-msg d-flex align-items-center justify-content-center">
                              <span>No company LinkedIn found on website</span>
                              <button
                                type="button"
                                className="ceo-sniper-edit-icon-btn"
                                title="Add company LinkedIn URL"
                                onClick={() =>
                                  openManualOverrideModal(
                                    taskId,
                                    "company",
                                    companyLinkedInUrl
                                  )
                                }
                              >
                                <i className="fa-solid fa-pen-to-square"></i>
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="arrow">
                          <i className="fa-solid fa-arrow-right"></i>
                        </div>
                        <div className="task-container">
                          {ceoFound ? (
                            ceoParseSuccess ? (
                              <>
                                <div className="inner-container">
                                  <CeoSniperPfp
                                    src={ceoImg}
                                    kind="ceo"
                                    alt={ceoName || "CEO"}
                                  />
                                  <div>
                                    <div className="info highlighted">
                                      {ceoName}
                                    </div>
                                    <div className="info">{ceoJobTitle}</div>
                                    <a
                                      href={companyCeoUrl}
                                      target="_blank"
                                      className="info"
                                    >
                                      {getLinkedInShortUrl(companyCeoUrl)}
                                    </a>
                                  </div>
                                </div>
                                {ceoAddedToSalesNav ? (
                                  <div className="employee-name-container sales-nav">
                                    <div className="employee-name">
                                      <i className="fa-solid fa-check"></i>{" "}
                                      Saved in
                                      Sales Navigator
                                    </div>
                                  </div>
                                ) : null}
                              </>
                            ) : (
                              <div className="error-msg">
                                Error scraping LinkedIn CEO data
                              </div>
                            )
                          ) : (
                            <div className="error-msg d-flex align-items-center justify-content-center">
                              <span>Company CEO could not be found</span>
                              <button
                                type="button"
                                className="ceo-sniper-edit-icon-btn"
                                title="Add CEO LinkedIn URL"
                                onClick={() =>
                                  openManualOverrideModal(
                                    taskId,
                                    "ceo",
                                    companyCeoUrl
                                  )
                                }
                              >
                                <i className="fa-solid fa-pen-to-square"></i>
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="handled-container">
                          {!ceoFound ? (
                            <>
                              {!handled ? (
                                <div
                                  className="d-flex justify-content-center align-items-center"
                                  // style={{ marginLeft: "18px" }}
                                >
                                  <button
                                    className={`run-btn btn btn-primary`}
                                    onClick={() => retryTask(taskId)}
                                    style={{ minWidth: "120px" }}
                                  >
                                    {`Retry`}
                                    <i className="event-owner-add fa-sharp fa-solid fa-arrows-rotate ml-05"></i>
                                  </button>
                                </div>
                              ) : null}
                              <div
                                className="form-check custom-checkbox checkbox-success check-lg handled-outer"
                                style={{ marginLeft: "18px" }}
                              >
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`handledCheckbox-${taskId}`}
                                  required
                                  checked={handled}
                                  onChange={() =>
                                    setTaskHandled(taskId, !handled)
                                  }
                                />
                                <label
                                  className="handled-label"
                                  htmlFor={`handledCheckbox-${taskId}`}
                                >
                                  Handled
                                </label>
                              </div>
                            </>
                          ) : null}
                          {ceoFound ? (
                            <button
                              className="btn btn-light btn-xs sharp ceo-sniper-retry-btn"
                              onClick={() => retryTask(taskId)}
                              title="Retry task"
                            >
                              <i className="fa-sharp fa-solid fa-arrows-rotate"></i>
                            </button>
                          ) : null}
                          <button
                            className="btn btn-danger btn-xs sharp ceo-sniper-delete-btn"
                            onClick={() => deleteTask(taskId)}
                            title="Delete task"
                            style={{ marginLeft: "10px" }}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })
                : null}
            </div>
          </div>
        ) : null}
        <Modal
          show={manualOverrideModal.open}
          onHide={closeManualOverrideModal}
          centered
          className="ceo-sniper-override-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {manualOverrideModal.mode === "company"
                ? "Set Company LinkedIn URL"
                : "Set CEO LinkedIn URL"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group mb-2">
              <label className="form-label">
                {manualOverrideModal.mode === "company"
                  ? "LinkedIn company URL"
                  : "LinkedIn profile URL"}
              </label>
              <input
                type="text"
                className="form-control"
                value={manualOverrideModal.url}
                onChange={(e) =>
                  setManualOverrideModal({
                    ...manualOverrideModal,
                    url: e.target.value,
                  })
                }
                placeholder={
                  manualOverrideModal.mode === "company"
                    ? "https://www.linkedin.com/company/example"
                    : "https://www.linkedin.com/in/example"
                }
              />
            </div>
            <div className="small text-muted highlighted">
              Saving this URL will queue the task to continue from this stage.
            </div>
            {manualOverrideModal.url.trim().length &&
            manualOverrideValidationError ? (
              <div className="small manual-override-error-text mt-1">
                {manualOverrideValidationError}
              </div>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-grey" onClick={closeManualOverrideModal}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmitManualOverride}
              disabled={disableManualOverrideSubmit}
            >
              Save and Resume
            </button>
          </Modal.Footer>
        </Modal>
      </motion.div>
    </>
  );
};

// column names (lowercased, spaces/underscores/hyphens stripped) that may
// contain the company website url - keep in sync with the backend
// (controllers/landmark/ceoSniperBatchManager.js)
const BATCH_URL_COLUMN_CANDIDATES = [
  "url",
  "website",
  "companyurl",
  "companywebsite",
  "websiteurl",
  "domain",
];

// extract a readable error message from an api response - handles express
// "Cannot GET/POST ..." html strings (endpoint missing, i.e. the bots server
// is running an older build) and auth failures, so we never toast "undefined"
function getApiErrorMessage(res, fallback = "Unknown error") {
  if (typeof res === "string") {
    return res.includes("Cannot ")
      ? "endpoint not found - has the bots server been updated?"
      : fallback;
  }
  return res?.error || res?.message || fallback;
}

function findBatchUrlColumn(headerFields) {
  const normalise = (name) =>
    String(name || "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "")
      .trim();
  for (const candidate of BATCH_URL_COLUMN_CANDIDATES) {
    const match = (headerFields || []).find((f) => normalise(f) === candidate);
    if (match) return match;
  }
  return null;
}

const CeoSniperBatchPanel = ({ employee }) => {
  const [jobs, setJobs] = useState([]);
  const [jobsLoaded, setJobsLoaded] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null); // { name, content, rowCount, urlColumn }
  const [fileError, setFileError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [generatingJobId, setGeneratingJobId] = useState("");
  const [stoppingJobId, setStoppingJobId] = useState("");
  const [queueStatus, setQueueStatus] = useState(null); // { paused, pausedReason, queueLength, processingTaskId, ... }
  const [queueActionBusy, setQueueActionBusy] = useState(false);
  const fileInputRef = useRef(null);

  async function fetchQueueStatus() {
    try {
      const api = new ApiService({ employee, botsApiUrl: true });
      const status = await api.get(`landmark/ceosniper/getQueueStatus`);
      if (status && typeof status.paused === "boolean") {
        setQueueStatus(status);
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function fetchJobs() {
    try {
      const api = new ApiService({ employee, botsApiUrl: true });
      const jobArr = await api.get(`landmark/ceosniper/getBatchJobs`);
      if (!Array.isArray(jobArr)) {
        console.error(`fetchJobs() failed:`, jobArr);
        toast.error(`Failed to load batch jobs (${getApiErrorMessage(jobArr)})`);
        return;
      }
      setJobs(jobArr);
      setJobsLoaded(true);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load batch jobs");
    }
    fetchQueueStatus();
  }

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // poll while any job is still processing (or the queue is paused, so the
  // banner clears once someone resumes it)
  useEffect(() => {
    const anyProcessing = jobs.some((j) => !j.isComplete);
    if (!anyProcessing && !queueStatus?.paused) return;
    const interval = setInterval(fetchJobs, 20000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, queueStatus?.paused]);

  const pauseQueue = async () => {
    if (queueActionBusy) return;
    setQueueActionBusy(true);
    try {
      const api = new ApiService({ employee, botsApiUrl: true });
      const res = await api.post(`landmark/ceosniper/pauseQueue`, {
        employee,
        reason: `Paused from dashboard by ${employee?.email || "employee"}`,
      });
      if (!res?.success) {
        toast.error(getApiErrorMessage(res, "Failed to pause the queue"));
        return;
      }
      toast.success(res.success, { duration: 5000 });
      setQueueStatus(res);
    } catch (error) {
      console.error(error);
      toast.error("Failed to pause the queue");
    } finally {
      setQueueActionBusy(false);
    }
  };

  const resumeQueue = async () => {
    if (queueActionBusy) return;
    setQueueActionBusy(true);
    try {
      const api = new ApiService({ employee, botsApiUrl: true });
      const res = await api.post(`landmark/ceosniper/resumeQueue`, {
        employee,
      });
      if (!res?.success) {
        toast.error(getApiErrorMessage(res, "Failed to resume the queue"));
        return;
      }
      toast.success(res.success, { duration: 5000 });
      setQueueStatus(res);
      fetchJobs();
    } catch (error) {
      console.error(error);
      toast.error("Failed to resume the queue");
    } finally {
      setQueueActionBusy(false);
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setFileError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setSelectedFile(null);
    setFileError("");
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setFileError("Please select a .csv file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      const parsed = Papa.parse(content, {
        header: true,
        skipEmptyLines: true,
      });
      const rows = parsed.data || [];
      const headerFields = parsed.meta?.fields || [];
      if (!rows.length) {
        setFileError("The CSV file contains no data rows");
        return;
      }
      const urlColumn = findBatchUrlColumn(headerFields);
      if (!urlColumn) {
        setFileError(
          `No company URL column found (expected a column like "url", "website" or "company url")`
        );
        return;
      }
      setSelectedFile({
        name: file.name,
        content,
        rowCount: rows.length,
        urlColumn,
      });
    };
    reader.onerror = () => setFileError("Could not read the selected file");
    reader.readAsText(file);
  };

  const handleUploadBtn = async () => {
    if (!selectedFile || uploading) return;
    setUploading(true);
    try {
      const api = new ApiService({ employee, botsApiUrl: true });
      const res = await api.post(`landmark/ceosniper/createBatchJob`, {
        employee,
        fileName: selectedFile.name,
        fileContent: selectedFile.content,
      });
      const success = res?.success;
      if (!success) {
        toast.error(getApiErrorMessage(res, "Failed to create batch job"));
        return;
      }
      toast.success(success, { duration: 6000 });
      clearSelectedFile();
      fetchJobs();
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload the CSV");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadResults = async (jobId) => {
    if (generatingJobId) return;
    setGeneratingJobId(jobId);
    try {
      const api = new ApiService({ employee, botsApiUrl: true });
      const res = await api.post(`landmark/ceosniper/generateBatchResults`, {
        employee,
        jobId,
      });
      const fileUrl = res?.fileUrl;
      if (!fileUrl) {
        toast.error(getApiErrorMessage(res, "Failed to generate results CSV"));
        return;
      }
      window.open(fileUrl, "_blank");
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate results CSV");
    } finally {
      setGeneratingJobId("");
    }
  };

  const retryJobFailures = async (jobId, retryCount) => {
    try {
      const confirmed = window.confirm(
        `Retry the ${retryCount} failed/cancelled ${
          retryCount === 1 ? "company" : "companies"
        } in this batch job?`
      );
      if (!confirmed) return;
      const api = new ApiService({ employee, botsApiUrl: true });
      const res = await api.post(`landmark/ceosniper/retryBatchJobFailures`, {
        employee,
        jobId,
      });
      const success = res?.success;
      if (!success) {
        toast.error(getApiErrorMessage(res, "Failed to retry failures"));
        return;
      }
      toast.success(success, { duration: 6000 });
      fetchJobs();
    } catch (error) {
      console.error(error);
      toast.error("Failed to retry batch job failures");
    }
  };

  const stopJob = async (jobId, pendingCount) => {
    if (stoppingJobId) return;
    try {
      const confirmed = window.confirm(
        `Stop this batch job? The ${pendingCount} ${
          pendingCount === 1 ? "company" : "companies"
        } still waiting will be cancelled (results so far are kept). You can resume later with "Retry Failures".`
      );
      if (!confirmed) return;
      setStoppingJobId(jobId);
      const api = new ApiService({ employee, botsApiUrl: true });
      const res = await api.post(`landmark/ceosniper/stopBatchJob`, {
        employee,
        jobId,
      });
      const success = res?.success;
      if (!success) {
        toast.error(getApiErrorMessage(res, "Failed to stop batch job"));
        return;
      }
      toast.success(success, { duration: 8000 });
      fetchJobs();
    } catch (error) {
      console.error(error);
      toast.error("Failed to stop batch job");
    } finally {
      setStoppingJobId("");
    }
  };

  const deleteJob = async (jobId) => {
    try {
      const confirmed = window.confirm(
        "Delete this batch job and all of its tasks? This cannot be undone."
      );
      if (!confirmed) return;
      const api = new ApiService({ employee, botsApiUrl: true });
      const res = await api.post(`landmark/ceosniper/deleteBatchJob`, {
        employee,
        jobId,
      });
      const success = res?.success;
      if (!success) {
        toast.error(getApiErrorMessage(res, "Failed to delete batch job"));
        return;
      }
      toast.success(success, { duration: 6000 });
      fetchJobs();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete batch job");
    }
  };

  return (
    <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
      <Card className="mb-3 h-fc bot-table">
        <div className="logo-container">
          <img src={SeamlessBotImg} className="img-fluid" />
        </div>
        <div className="sub-logo-text-container">
          <div>
            • Upload a CSV containing a company URL column (e.g. "url" or
            "website")
            <br />
            • For each company the bot will find the LinkedIn company page and
            the CEO's LinkedIn profile
            <br />
            • Download the enriched list once processing completes (failed rows
            are kept, sorted to the bottom)
            <br />
          </div>
        </div>
        <div className="ceo-sniper-batch-upload">
          <input
            type="file"
            accept=".csv,text/csv"
            ref={fileInputRef}
            className="form-control"
            onChange={handleFileChange}
          />
          {fileError ? (
            <div className="batch-file-error">{fileError}</div>
          ) : null}
          {selectedFile ? (
            <div className="batch-file-info">
              <b>{selectedFile.name}</b> - {addCommas(selectedFile.rowCount)}{" "}
              {selectedFile.rowCount === 1 ? "company" : "companies"} (URL
              column: "{selectedFile.urlColumn}")
            </div>
          ) : null}
          <div className="d-flex justify-content-center align-items-center mt-3">
            <button
              className={`run-btn btn btn-primary ${
                selectedFile && !uploading ? "" : "disabled-soft"
              }`}
              onClick={handleUploadBtn}
            >
              {uploading ? `UPLOADING...` : `RUN BATCH`}
              <i className="event-owner-add fa-sharp fa-solid fa-robot ml-05"></i>
            </button>
          </div>
        </div>
      </Card>
      {queueStatus ? (
        <div
          className={`ceo-sniper-queue-status ${
            queueStatus.paused ? "paused" : ""
          }`}
        >
          <div className="queue-status-text">
            {queueStatus.paused ? (
              <>
                <i className="fa-solid fa-circle-pause"></i>{" "}
                <b>Queue paused</b>
                {queueStatus.pausedBy ? ` by ${queueStatus.pausedBy}` : ""}
                {queueStatus.pausedReason ? (
                  <div className="queue-status-reason">
                    {queueStatus.pausedReason}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <i className="fa-solid fa-circle-play"></i>{" "}
                <b>Queue running</b> - {addCommas(queueStatus.queueLength)}{" "}
                waiting
                {queueStatus.processingCompanyUrl
                  ? ` - currently: ${queueStatus.processingCompanyUrl}`
                  : ""}
              </>
            )}
          </div>
          {queueStatus.paused ? (
            <button
              className="btn btn-success btn-xs"
              onClick={resumeQueue}
              disabled={queueActionBusy}
              title="Resume processing queued tasks"
            >
              <i className="fa-solid fa-play"></i> Resume Queue
            </button>
          ) : (
            <button
              className="btn btn-light btn-xs"
              onClick={pauseQueue}
              disabled={queueActionBusy}
              title="Pause the queue (the current task finishes, nothing new starts)"
            >
              <i className="fa-solid fa-pause"></i> Pause Queue
            </button>
          )}
        </div>
      ) : null}
      {jobsLoaded && !jobs.length ? (
        <div className="text-center text-muted mb-4">No batch jobs yet</div>
      ) : null}
      {jobs.length ? (
        <div className="ceo-sniper-tasks-section ceo-sniper-batch-jobs">
          <div className="header-text">Batch Jobs</div>
          <div className="tasks-container">
            {jobs.map((job) => {
              const {
                jobId,
                fileName,
                originalFileUrl,
                employeeEmail,
                createdTime,
                totalRows,
                invalidRows,
                pending,
                processed,
                success,
                failed,
                cancelled,
                isComplete,
                isStopped,
              } = job;
              const progressPct = totalRows
                ? Math.round((processed / totalRows) * 100)
                : 0;
              // invalid-url rows never had a task, so they can't be retried;
              // cancelled rows can (that's how a stopped job is resumed)
              const retryableFailed =
                Math.max(failed - (invalidRows || 0), 0) + (cancelled || 0);
              return (
                <div className="batch-job-container" key={jobId}>
                  <div className="batch-job-info">
                    <div className="batch-job-filename">{fileName}</div>
                    {employeeEmail ? (
                      <div className="employee-name-container">
                        <div className="employee-name">
                          <b>
                            {employeeEmail
                              .toLowerCase()
                              .replace("@landmarkventures.com", "")}
                          </b>{" "}
                          -{" "}
                          {epochToDateString(createdTime, {
                            simpleUS: true,
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="batch-job-progress">
                    <div className="batch-job-status-line">
                      {isComplete && isStopped ? (
                        <Badge bg="secondary">Stopped</Badge>
                      ) : isComplete ? (
                        <Badge bg="success">Completed</Badge>
                      ) : isStopped ? (
                        <Badge bg="secondary">Stopping...</Badge>
                      ) : (
                        <Badge bg="warning">Processing</Badge>
                      )}
                      <span className="batch-job-counts">
                        {addCommas(processed)}/{addCommas(totalRows)} processed
                      </span>
                    </div>
                    <ProgressBar
                      now={progressPct}
                      label={`${progressPct}%`}
                      variant={
                        isStopped ? "secondary" : isComplete ? "success" : "info"
                      }
                    />
                    <div className="batch-job-counts-line">
                      <span className="success-count">
                        <i className="fa-solid fa-check"></i>{" "}
                        {addCommas(success)} CEO
                        {success === 1 ? "" : "s"} found
                      </span>
                      <span className="fail-count">
                        <i className="fa-solid fa-xmark"></i>{" "}
                        {addCommas(failed)} failed
                      </span>
                      {cancelled ? (
                        <span className="cancelled-count">
                          <i className="fa-solid fa-ban"></i>{" "}
                          {addCommas(cancelled)} cancelled
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="batch-job-actions">
                    <a
                      className="btn btn-light btn-xs"
                      href={originalFileUrl}
                      target="_blank"
                      rel="noreferrer"
                      title="Download the original uploaded CSV"
                    >
                      <i className="fa-solid fa-file-csv"></i> Original CSV
                    </a>
                    <button
                      className="btn btn-primary btn-xs"
                      onClick={() => handleDownloadResults(jobId)}
                      disabled={generatingJobId === jobId}
                      title="Download the enriched CSV with CEO info"
                    >
                      <i className="fa-solid fa-download"></i>{" "}
                      {generatingJobId === jobId
                        ? "Generating..."
                        : "Results CSV"}
                    </button>
                    {!isComplete && pending > 0 ? (
                      <button
                        className="btn btn-danger btn-xs"
                        onClick={() => stopJob(jobId, pending)}
                        disabled={stoppingJobId === jobId || isStopped}
                        title="Stop this job - cancels the companies still waiting (results so far are kept)"
                      >
                        <i className="fa-solid fa-stop"></i>{" "}
                        {stoppingJobId === jobId || isStopped
                          ? "Stopping..."
                          : `Stop (${addCommas(pending)} left)`}
                      </button>
                    ) : null}
                    {isComplete && retryableFailed > 0 ? (
                      <button
                        className="btn btn-warning btn-xs"
                        onClick={() => retryJobFailures(jobId, retryableFailed)}
                        title={`Retry the ${retryableFailed} failed/cancelled ${
                          retryableFailed === 1 ? "company" : "companies"
                        }`}
                      >
                        <i className="fa-sharp fa-solid fa-arrows-rotate"></i>{" "}
                        {cancelled ? "Resume / Retry" : "Retry Failures"} (
                        {retryableFailed})
                      </button>
                    ) : null}
                    <button
                      className="btn btn-danger btn-xs sharp ceo-sniper-delete-btn"
                      onClick={() => deleteJob(jobId)}
                      title="Delete batch job"
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </motion.div>
  );
};

const PasswordPrompt = ({ employee, setPassword, submitPassword }) => {
  const handleInputChange = (e) => {
    setPassword(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitPassword(employee);
  };

  return (
    <div className="d-flex">
      <input
        onChange={handleInputChange}
        type="employee"
        className="form-control w-100 mt-1 inline-input"
        placeholder="Password"
        value={employee}
        autoComplete="employee"
      ></input>
      <div
        className="btn btn-primary light btn-xs mt-1 me-1 w-fc inline-btn"
        onClick={(e) => handleSubmit(e)}
      >
        Submit
      </div>
    </div>
  );
};

const Dashboard = (props) => {
  const { changeBackground } = useContext(ThemeContext);
  const { employee, isAuthenticated, loading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("sniper"); // "sniper" | "batch"

  // set light theme (once)
  useEffect(() => {
    changeBackground({ value: "light", label: "Light" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // touch lastSeenTime on backend when authenticated (fire & forget)
  useEffect(() => {
    if (!isAuthenticated || !employee) return;
    try {
      const api = new ApiService({ employee });
      api.get(`landmark/employees/login`);
    } catch (error) {
      console.warn(`employee login touch failed`, error);
    }
  }, [isAuthenticated, employee]);

  useDocumentTitle("CEO Sniper - Landmark Ventures");

  // Loading gate while AuthContext initializes
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border" role="status" aria-label="Loading" />
      </div>
    );
  }

  return (
    <Fragment>
      <div className="global-dash-wrapper seamless-bot">
        <div className="d-flex align-items-center justify-content-center">
          <img src={landmarkImg} className="img-fluid landmark-img" alt="Landmark" />
        </div>

        {isAuthenticated && employee && (
          <div className="landmark-sign-in" style={{ marginBottom: "18px" }}>
            <div className="existing-accounts-container">
              <EmployeeInfo
                landmarkEmployee={employee}
                disableHover={true}
                showLogout={true}
                logout={logout}
              />
            </div>
          </div>
        )}

        <PageHeaderBar
          pages={[
            { title: "Landmark", url: `` },
            { title: "LinkedIn CEO Sniper Bot", url: `/landmark/ceosniper` },
          ]}
        />

        {/* show dashboard when authenticated via AuthContext */}
        {isAuthenticated ? (
          <>
            <div className="ceo-sniper-tabs">
              <button
                type="button"
                className={`ceo-sniper-tab-btn ${
                  activeTab === "sniper" ? "active" : ""
                }`}
                onClick={() => setActiveTab("sniper")}
              >
                <i className="fa-sharp fa-solid fa-crosshairs"></i> CEO Sniper
              </button>
              <button
                type="button"
                className={`ceo-sniper-tab-btn ${
                  activeTab === "batch" ? "active" : ""
                }`}
                onClick={() => setActiveTab("batch")}
              >
                <i className="fa-solid fa-file-csv"></i> CSV Batch
              </button>
            </div>
            {activeTab === "sniper" ? (
              <SeamlessBotForm employee={employee} />
            ) : (
              <CeoSniperBatchPanel employee={employee} />
            )}
          </>
        ) : (
          <div className="text-center text-muted p-5">
            Please sign in to access the tool.
          </div>
        )}
      </div>
    </Fragment>
  );
};


export default Dashboard;
